import axios from "../../../utils/axios";
import { handleRemoveEmptyParamFromQueryParams } from "../../../utils/helpers";
import { IApiResponse } from "../../../utils/types/api-response";
import {
    IClientAllergyListFilters,
    IClientMedicationsListFilters,
    IClientPrescriptionEventListFilters,
    ICreateDraftLabRequestPayload,
    IEditDraftLabRequestPayload,
    ISignLabRequestPayload,
    IDrFirstNotificationCountData,
    ILabCategoryListFilters,
    ILabCompanyTestListFilters,
    ILabRequestListFilters,
    ILabTestListFilters,
    IPrescriptionListFilters,
    ISendLabRequestPayload,
    ISSOLaunchPayload,
    ISyncPrescriptionDataPayload,
    ITriggerClientSyncPayload,
} from "../types/e-prescribe.types";

export async function triggerSSOLaunch(
    practiceId: string,
    payload?: ISSOLaunchPayload
) {
    const response = await axios.post(
        `/practice/${practiceId}/drfirst/sso-launch`,
        payload
    );
    return response.data;
}

export async function triggerClientSync(
    practiceId: string,
    clientId: string,
    payload: ITriggerClientSyncPayload
) {
    const response = await axios.post(
        `/practice/${practiceId}/drfirst/sync-patient/${clientId}`,
        payload
    );
    return response.data;
}

export async function syncClientClinicalData(
    practiceId: string,
    clientId: string
) {
    const response = await axios.post(
        `/practice/${practiceId}/drfirst/sync-clinical-data/${clientId}`
    );
    return response.data;
}

export async function getClientSyncStatus(
    practiceId: string,
    clientId: string
) {
    const response = await axios.get(
        `/practice/${practiceId}/drfirst/sync-status/${clientId}`
    );
    return response.data;
}

export async function getClientAllergies(
    practiceId: string,
    clientId: string,
    filters?: IClientAllergyListFilters
) {
    const response = await axios.get(
        `/practice/${practiceId}/clients/${clientId}/allergies`,
        { params: handleRemoveEmptyParamFromQueryParams(filters ?? {}) }
    );
    return response.data;
}

export async function getClientMedications(
    practiceId: string,
    clientId: string,
    filters?: IClientMedicationsListFilters
) {
    const response = await axios.get(
        `/practice/${practiceId}/clients/${clientId}/medications`,
        { params: handleRemoveEmptyParamFromQueryParams(filters ?? {}) }
    );
    return response.data;
}

export async function getPrescriptions(
    practiceId: string,
    filters?: IPrescriptionListFilters
) {
    const response = await axios.get(
        `/practice/${practiceId}/clients/prescriptions`,
        { params: handleRemoveEmptyParamFromQueryParams(filters ?? {}) }
    );
    return response.data;
}

export async function syncPrescriptionData(
    practiceId: string,
    clientId: string,
    payload: ISyncPrescriptionDataPayload
) {
    const response = await axios.post(
        `/practice/${practiceId}/drfirst/sync-prescription-data/${clientId}`,
        payload
    );
    return response.data;
}

export async function getClientPrescriptionEvents(
    practiceId: string,
    clientId: string,
    filters?: IClientPrescriptionEventListFilters
) {
    const response = await axios.get(
        `/practice/${practiceId}/clients/${clientId}/prescription-events`,
        { params: handleRemoveEmptyParamFromQueryParams(filters ?? {}) }
    );
    return response.data;
}

export async function getDrFirstNotificationCount(
    practiceId: string
): Promise<IApiResponse<IDrFirstNotificationCountData>> {
    const response = await axios.get(
        `/practice/${practiceId}/drfirst/notification-count`
    );
    return response.data;
}

export async function syncPracticeLevelPrescriptions(practiceId: string) {
    const response = await axios.post(`/practice/${practiceId}/drfirst/sync`);

    return response.data;
}

export async function getLabRequests(filters?: ILabRequestListFilters) {
    const response = await axios.get(`/lab-requests`, {
        params: handleRemoveEmptyParamFromQueryParams(filters ?? {}),
    });
    return response.data;
}

export async function getLabCompanies() {
    const response = await axios.get(`/lab-companies`);
    return response.data;
}

export async function getLabCategories(filters?: ILabCategoryListFilters) {
    const response = await axios.get(`/lab-categories`, {
        params: handleRemoveEmptyParamFromQueryParams(filters ?? {}),
    });
    return response.data;
}

export async function getLabTests(filters?: ILabTestListFilters) {
    const response = await axios.get(`/lab-tests`, {
        params: handleRemoveEmptyParamFromQueryParams(filters ?? {}),
    });
    return response.data;
}

export async function getLabCompanyTests(filters?: ILabCompanyTestListFilters) {
    const response = await axios.get(`/lab-company-tests`, {
        params: handleRemoveEmptyParamFromQueryParams(filters ?? {}),
    });
    return response.data;
}

export async function createDraftLabRequest(
    payload: ICreateDraftLabRequestPayload
) {
    const response = await axios.post(`/lab-requests`, payload);
    return response.data;
}

export async function getLabRequest(labRequestId: string) {
    const response = await axios.get(`/lab-requests/${labRequestId}`);
    return response.data;
}

export async function updateLabRequest(
    labRequestId: string,
    payload: IEditDraftLabRequestPayload
) {
    const response = await axios.patch(
        `/lab-requests/${labRequestId}`,
        payload
    );
    return response.data;
}

export async function signLabRequest(
    labRequestId: string,
    payload: ISignLabRequestPayload
) {
    const response = await axios.post(
        `/lab-requests/${labRequestId}/sign`,
        payload
    );
    return response.data;
}

export async function downloadLabRequest(labRequestId: string) {
    const response = await axios.get(`/lab-requests/${labRequestId}/download`, {
        responseType: "blob",
    });
    return response.data;
}

export async function sendLabRequest(
    labRequestId: string,
    payload: ISendLabRequestPayload
) {
    const response = await axios.post(
        `/lab-requests/${labRequestId}/send`,
        payload
    );
    return response.data;
}
